'use client';

/**
 * ReferPharmacyCTA
 * ─────────────────────────────────────────────────────────────────────────────
 * Lightweight, reusable CTA that surfaces the InvitePharmacy feature across
 * patient-facing screens. On click: records the click (trackEvent) and opens a
 * modal wrapping InvitePharmacyForm, loaded on first open (InvitePharmacyModal).
 *
 * Phase 1 - 2026-07-20. Julian approved.
 *
 * Props:
 *   variant - visual treatment (card | inline | sidebar | banner | empty-state |
 *              post-request)
 *   context - analytics label (zero-bids | low-bids | post-upload | city-empty |
 *              dashboard | thank-you | footer | homepage | post-request)
 *   className - optional Tailwind override
 *
 * Compliance:
 *   - NO incentive / reward language (deferred - Julian hasn't approved $10 yet)
 *   - NO TCPA, NO Adam Reinhard references (R10)
 *   - Voice: patient-empathy, calm, direct
 */

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Building2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';

// The dialog and its form load on first open (see InvitePharmacyModal.tsx).
const InvitePharmacyModal = dynamic(() => import('./InvitePharmacyModal'), { ssr: false });

// ── Types ─────────────────────────────────────────────────────────────────────

export type ReferPharmacyCTAVariant =
  | 'card'
  | 'inline'
  | 'sidebar'
  | 'banner'
  | 'empty-state'
  | 'post-request';

export type ReferPharmacyCTAContext =
  | 'zero-bids'
  | 'low-bids'
  | 'post-upload'
  | 'city-empty'
  | 'dashboard'
  | 'thank-you'
  | 'footer'
  | 'homepage'
  | string; // allow arbitrary strings for future surfaces

export interface ReferPharmacyCTAProps {
  variant: ReferPharmacyCTAVariant;
  context: ReferPharmacyCTAContext;
  className?: string;
  /**
   * Open the invitation form straight away when the page URL ends in this
   * fragment (without the #). The no-quote email links to
   * /no-bids-help#invite-your-pharmacy.
   */
  openOnHash?: string;
}

// ── Copy configuration per variant ───────────────────────────────────────────
// These are defaults. Call sites can override via className but not copy here - 
// copy changes require a PR + Julian review.

interface VariantCopy {
  headline: string;
  body?: string;
  button: string;
}

const COPY: Record<ReferPharmacyCTAVariant, VariantCopy> = {
  card: {
    headline: 'Your local pharmacy could be here',
    body: 'Invite them to compete for your prescriptions.',
    button: 'Invite a Pharmacy',
  },
  inline: {
    headline: '',
    button: 'Refer your local pharmacy →',
  },
  sidebar: {
    headline: 'Missing your pharmacy?',
    body: 'Help them join Script Unlock.',
    button: 'Invite a Pharmacy',
  },
  banner: {
    headline: 'Help your local pharmacy get in front of savings-focused patients',
    body: undefined,
    button: 'Refer Your Local Pharmacy →',
  },
  'empty-state': {
    headline: 'No quotes yet? Let\'s fix that.',
    body: 'Invite your local pharmacy to join Script Unlock - they might be exactly who you need.',
    button: 'Refer Your Local Pharmacy',
  },
  // Right after a request is sent (/prescription-live), when the patient has
  // their usual pharmacy in mind.
  'post-request': {
    headline: 'Want your own pharmacy to quote?',
    body: 'Invite the pharmacy you already use. Its name and town are enough.',
    button: 'Invite my pharmacy',
  },
};

// ── Variant renderers ─────────────────────────────────────────────────────────

function CardVariant({ onOpen, copy }: { onOpen: () => void; copy: VariantCopy }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 flex flex-col gap-3">
      <div className="flex items-center gap-2 text-primary">
        <Building2 className="h-5 w-5 flex-shrink-0" />
        <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Grow Our Network
        </span>
      </div>
      <h3 className="font-display font-bold text-foreground text-lg leading-snug">
        {copy.headline}
      </h3>
      {copy.body && (
        <p className="text-sm text-muted-foreground leading-relaxed">{copy.body}</p>
      )}
      <Button
        variant="default"
        size="sm"
        className="self-start mt-1"
        onClick={onOpen}
      >
        <Building2 className="h-4 w-4 mr-2" />
        {copy.button}
      </Button>
    </div>
  );
}

function InlineVariant({ onOpen, copy }: { onOpen: () => void; copy: VariantCopy }) {
  return (
    <button
      onClick={onOpen}
      className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
      type="button"
    >
      {copy.button}
    </button>
  );
}

function SidebarVariant({ onOpen, copy }: { onOpen: () => void; copy: VariantCopy }) {
  return (
    <div className="rounded-xl border border-border/60 bg-muted/30 p-4 flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
        <span className="text-sm font-semibold text-foreground">{copy.headline}</span>
      </div>
      {copy.body && (
        <p className="text-xs text-muted-foreground leading-relaxed">{copy.body}</p>
      )}
      <Button variant="outline" size="sm" className="w-full text-xs" onClick={onOpen}>
        {copy.button}
      </Button>
    </div>
  );
}

function BannerVariant({ onOpen, copy }: { onOpen: () => void; copy: VariantCopy }) {
  return (
    <div className="w-full bg-primary/5 border border-primary/20 rounded-2xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
      <Building2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5 sm:mt-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground leading-snug">
          {copy.headline}
        </p>
      </div>
      <Button
        variant="default"
        size="sm"
        className="flex-shrink-0 whitespace-nowrap"
        onClick={onOpen}
      >
        {copy.button}
        <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
      </Button>
    </div>
  );
}

function EmptyStateVariant({ onOpen, copy }: { onOpen: () => void; copy: VariantCopy }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-8 flex flex-col items-center text-center gap-4 max-w-sm mx-auto">
      {/* Illustration placeholder - swap for SVG asset when available */}
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
        <Building2 className="h-8 w-8 text-primary" />
      </div>
      <div className="space-y-1.5">
        <h3 className="font-display font-bold text-foreground text-lg">
          {copy.headline}
        </h3>
        {copy.body && (
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
            {copy.body}
          </p>
        )}
      </div>
      <Button variant="default" size="sm" className="mt-1" onClick={onOpen}>
        <Building2 className="h-4 w-4 mr-2" />
        {copy.button}
      </Button>
    </div>
  );
}

function PostRequestVariant({ onOpen, copy }: { onOpen: () => void; copy: VariantCopy }) {
  return (
    <div className="rounded-xl border border-primary/25 bg-primary/5 p-4 flex items-start gap-3">
      <Building2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">{copy.headline}</p>
        {copy.body && <p className="text-sm text-muted-foreground mt-0.5">{copy.body}</p>}
        <Button variant="default" size="sm" className="mt-3" onClick={onOpen}>
          {copy.button}
        </Button>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function ReferPharmacyCTA({ variant, context, className, openOnHash }: ReferPharmacyCTAProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoaded, setModalLoaded] = useState(false);

  const handleOpen = () => {
    trackEvent('refer_pharmacy_cta_clicked', { context, variant });
    setModalLoaded(true);
    setModalOpen(true);
  };

  useEffect(() => {
    if (!openOnHash || window.location.hash !== `#${openOnHash}`) return;
    trackEvent('refer_pharmacy_cta_clicked', { context, variant, via: 'link' });
    setModalLoaded(true);
    setModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOnHash]);

  const copy = COPY[variant];

  const inner = (() => {
    switch (variant) {
      case 'card':
        return <CardVariant onOpen={handleOpen} copy={copy} />;
      case 'inline':
        return <InlineVariant onOpen={handleOpen} copy={copy} />;
      case 'sidebar':
        return <SidebarVariant onOpen={handleOpen} copy={copy} />;
      case 'banner':
        return <BannerVariant onOpen={handleOpen} copy={copy} />;
      case 'empty-state':
        return <EmptyStateVariant onOpen={handleOpen} copy={copy} />;
      case 'post-request':
        return <PostRequestVariant onOpen={handleOpen} copy={copy} />;
    }
  })();

  return (
    <>
      <div className={className}>{inner}</div>
      {modalLoaded && (
        <InvitePharmacyModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          context={context}
        />
      )}
    </>
  );
}

export default ReferPharmacyCTA;
